import {useEffect,useRef,useState} from 'react';
import {Play,Pause} from 'lucide-react';
import {API} from '../catalogue/client';
import type {Story} from '../stories';
import type {NarrationTrack} from '../../shared/narration';
import {narrationHash,narrationPrefix,narrationTrack,narrationText,narrationIndexAt} from '../../shared/narration';

const manifests=new Map<string,Promise<unknown>>();
function loadManifest(url:string){
 if(!manifests.has(url)){
  const promise=fetch(url,{cache:'no-cache',signal:AbortSignal.timeout(10000)}).then(response=>{
   if(!response.ok)throw Error('Narration unavailable');return response.json();
  }).catch(error=>{manifests.delete(url);throw error;});
  manifests.set(url,promise);
 }
 return manifests.get(url)!;
}
export function NarrationPlayer({story,index,language,suspended,focused,onFocus,onSlide}:{story:Story;index:number;language:string;suspended:boolean;focused:boolean;onFocus:()=>void;onSlide:(index:number)=>void}){
 const player=useRef<HTMLAudioElement>(null),timelineIndex=useRef(index),pendingSeek=useRef<number|null>(null);
 const callbacks=useRef({onFocus,onSlide});callbacks.current={onFocus,onSlide};
 const ownsFullscreen=useRef(false),wasFocused=useRef(focused);
 const [track,setTrack]=useState<NarrationTrack|null>(null),[playing,setPlaying]=useState(false),[failed,setFailed]=useState(false);
 const prefix=language==='en'?narrationPrefix(story):null;
 const source=prefix&&track?API+'/audio/'+prefix+'/'+track.file:'';
 useEffect(()=>{
  let active=true;setTrack(null);setFailed(false);
  if(prefix)Promise.all([loadManifest(API+'/audio/'+prefix+'/manifest.json'),Promise.all(story.slides.map((_,i)=>narrationHash(narrationText(story,i))))]).then(([manifest,hashes])=>{
   if(active)setTrack(narrationTrack(manifest,story,hashes));
  }).catch(()=>{/* Missing narration never interrupts reading. */});
  return()=>{active=false;};
 },[prefix,story]);
 // The element and its source survive slide changes and entry into full view.
 useEffect(()=>{
  const audio=player.current;
  const pause=()=>{if(document.hidden)audio?.pause();};
  document.addEventListener('visibilitychange',pause);
  return()=>{
   document.removeEventListener('visibilitychange',pause);audio?.pause();
   if(audio){audio.removeAttribute('src');audio.load();}
   if(ownsFullscreen.current&&document.fullscreenElement)void document.exitFullscreen().catch(()=>{});
  };
 },[source]);
 useEffect(()=>{if(suspended)player.current?.pause();},[suspended]);
 useEffect(()=>{
  if(wasFocused.current&&!focused){player.current?.pause();if(ownsFullscreen.current&&document.fullscreenElement)void document.exitFullscreen().catch(()=>{});}
  wasFocused.current=focused;
 },[focused]);
 useEffect(()=>{
  if(!track)return;
  // Audio-driven navigation has already moved this pointer. Manual navigation
  // seeks within the same track and preserves its play/pause state.
  if(timelineIndex.current===index)return;
  timelineIndex.current=index;pendingSeek.current=track.cues[index].startSeconds;
  const audio=player.current;if(audio&&audio.readyState>0){audio.currentTime=pendingSeek.current;pendingSeek.current=null;}
 },[index,track]);
 function toggle(){
  const audio=player.current;if(!audio||!track||suspended)return;
  if(!audio.paused){audio.pause();return;}
  if(audio.readyState===0||audio.ended){pendingSeek.current=track.cues[index].startSeconds;if(audio.readyState>0){audio.currentTime=pendingSeek.current;pendingSeek.current=null;}}
  callbacks.current.onFocus();
  void audio.play().catch(()=>setPlaying(false));
  // The app still enters full view when browser full screen is unsupported.
  // Pause leaves both the layout and browser full-screen state untouched.
  if(!document.fullscreenElement&&document.fullscreenEnabled){
   void document.documentElement.requestFullscreen().then(()=>{ownsFullscreen.current=true;}).catch(()=>{});
  }
 }
 function followAudio(){
  const audio=player.current;if(!audio||!track||audio.paused||audio.seeking||pendingSeek.current!==null||suspended)return;
  const next=narrationIndexAt(track,audio.currentTime);
  if(next!==timelineIndex.current){timelineIndex.current=next;callbacks.current.onSlide(next);}
 }
 if(!source||failed)return null;
 return <div className="narration-control">
  <button className={'narration-toggle '+(playing?'is-playing':'')} aria-label={playing?'Pause narration':'Play narration'} title={playing?'Pause narration':'Play narration'} aria-pressed={playing} disabled={suspended} onClick={toggle}>{playing?<Pause size={18}/>:<Play size={18}/>}<span>{playing?'Pause':'Play'}</span></button>
  <audio ref={player} aria-label="Story narration" preload="none" src={source} onLoadedMetadata={()=>{if(pendingSeek.current!==null){player.current!.currentTime=pendingSeek.current;pendingSeek.current=null;}}} onTimeUpdate={followAudio} onError={()=>{setFailed(true);setPlaying(false);}} onPlay={()=>{if(suspended||document.hidden)player.current?.pause();else setPlaying(true);}} onPause={()=>setPlaying(false)} onEnded={()=>setPlaying(false)}/>
 </div>;
}
